import type { Point3D, SegmentDelta, TypedInputMode } from "./types";

/**
 * The two ways every tool lets you type a point relative to an origin — the
 * last fixed vertex for the draw tools, the base point for MOVE/COPY.
 *
 * Deliberately exactly two, and mutually exclusive. The previous design let the
 * draw tools accept any 2 of {dx, dy, length, angleDeg} and solved for the
 * other two, which meant two of the six combinations were sign-ambiguous
 * (guessed from the last resolved segment) and two more were unsolvable at
 * particular angles, failing silently. X/Y and L/angle are each independently
 * determined, so a typed pair always means exactly one point.
 */

/** Cartesian: signed offsets along each axis. */
export const XY_MODE: TypedInputMode = {
  id: "xy",
  fields: [
    { key: "dx", label: "X" },
    { key: "dy", label: "Y" },
  ],
};

/** Polar: distance plus absolute bearing, AutoCAD's `@d<a`. */
export const LENGTH_ANGLE_MODE: TypedInputMode = {
  id: "lengthAngle",
  fields: [
    { key: "length", label: "L", allowNegative: false },
    { key: "angleDeg", icon: "angle" },
  ],
};

/** Both modes, for any step that places a free point relative to an origin. */
export const RELATIVE_POINT_MODES: TypedInputMode[] = [
  XY_MODE,
  LENGTH_ANGLE_MODE,
];

const DEG = 180 / Math.PI;

/**
 * Live values of the preview point relative to the origin, for mirroring into
 * the pad. Returns all four keys — the superset of both modes — so switching
 * mode shows a filled-in field straight away rather than a blank one.
 */
export function relativePointValues(
  origin: Point3D | null,
  current: Point3D | null
): Record<string, number> {
  if (!origin || !current) return { dx: 0, dy: 0, length: 0, angleDeg: 0 };
  const dx = current.x - origin.x;
  const dy = current.y - origin.y;
  const length = Math.hypot(dx, dy);
  // A zero-length segment has no bearing; report 0 rather than atan2's 0-by-0.
  return { dx, dy, length, angleDeg: length > 0 ? Math.atan2(dy, dx) * DEG : 0 };
}

/**
 * The world-space delta a typed pair stands for, or null if either value is
 * unusable (mid-typing, or a negative length the pad shouldn't have produced).
 */
export function deltaFromRelativeInput(
  modeId: string,
  values: Record<string, number>
): SegmentDelta | null {
  if (modeId === LENGTH_ANGLE_MODE.id) {
    const { length, angleDeg } = values;
    if (!Number.isFinite(length) || !Number.isFinite(angleDeg)) return null;
    if (length < 0) return null;
    const theta = angleDeg / DEG;
    return { dx: length * Math.cos(theta), dy: length * Math.sin(theta) };
  }
  const { dx, dy } = values;
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
  return { dx, dy };
}

/** The point that delta lands on, keeping the origin's Z. */
export function pointFromRelativeInput(
  origin: Point3D | null,
  modeId: string,
  values: Record<string, number>
): Point3D | null {
  if (!origin) return null;
  const delta = deltaFromRelativeInput(modeId, values);
  if (!delta) return null;
  return { x: origin.x + delta.dx, y: origin.y + delta.dy, z: origin.z };
}
